import { useCallback, useState } from 'react';
import {
  checkPersonalIdentityVerification,
  finishGallogSignup,
  getPersonalCodeAuthError,
  PERSONAL_AUTH_PROVIDERS,
  signInWithPersonalCode,
  startPersonalIdentityVerification,
} from '../api/personalCodeAuthApi.js';

const EMPTY_NOTICE = { type: '', message: '' };

function providerCopy(provider) {
  if (provider === PERSONAL_AUTH_PROVIDERS.ARCA) {
    return {
      empty: '아카라이브 닉네임을 입력해주세요.',
      started: '아카라이브 프로필 상태 메시지에 아래 인증코드를 입력한 뒤 확인을 눌러주세요.',
      verified: '아카라이브 인증이 완료됐습니다. 개인 로그인 코드를 안전하게 저장해주세요.',
    };
  }
  return {
    empty: 'DC 갤로그 식별코드를 입력해주세요.',
    started: 'DC에 로그인한 본인 계정으로 갤로그 방명록에 아래 인증코드를 입력한 뒤 확인을 눌러주세요.',
    verified: '갤로그 작성자 확인이 완료됐습니다. 개인 로그인 코드를 안전하게 저장해주세요.',
  };
}

export function usePersonalCodeAuthForm() {
  const [mode, setMode] = useState('login');
  const [loginCode, setLoginCode] = useState('');
  const [signupProvider, setSignupProvider] = useState(PERSONAL_AUTH_PROVIDERS.DC_GALLOG);
  const [identityValue, setIdentityValue] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [signupResult, setSignupResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(EMPTY_NOTICE);

  const changeMode = useCallback((nextMode) => {
    setMode(nextMode);
    setNotice(EMPTY_NOTICE);
    setChallenge(null);
    setSignupResult(null);
  }, []);

  const changeSignupProvider = useCallback((nextProvider) => {
    const normalized = nextProvider === PERSONAL_AUTH_PROVIDERS.ARCA
      ? PERSONAL_AUTH_PROVIDERS.ARCA
      : PERSONAL_AUTH_PROVIDERS.DC_GALLOG;
    setSignupProvider(normalized);
    setIdentityValue('');
    setChallenge(null);
    setSignupResult(null);
    setNotice(EMPTY_NOTICE);
  }, []);

  const submitLogin = useCallback(async (event) => {
    event.preventDefault();
    if (!loginCode.trim()) {
      setNotice({ type: 'error', message: '개인 로그인 코드를 입력해주세요.' });
      return;
    }
    setSubmitting(true);
    setNotice(EMPTY_NOTICE);
    try {
      await signInWithPersonalCode(loginCode);
    } catch (error) {
      setNotice({ type: 'error', message: getPersonalCodeAuthError(error) });
    } finally {
      setSubmitting(false);
    }
  }, [loginCode]);

  const startVerification = useCallback(async (event) => {
    event.preventDefault();
    const copy = providerCopy(signupProvider);
    if (!identityValue.trim()) {
      setNotice({ type: 'error', message: copy.empty });
      return;
    }
    setSubmitting(true);
    setNotice(EMPTY_NOTICE);
    try {
      const next = await startPersonalIdentityVerification(signupProvider, identityValue);
      setChallenge({ ...next, provider: next.provider || signupProvider });
      setNotice({ type: 'success', message: copy.started });
    } catch (error) {
      setNotice({ type: 'error', message: getPersonalCodeAuthError(error) });
    } finally {
      setSubmitting(false);
    }
  }, [identityValue, signupProvider]);

  const verifyIdentity = useCallback(async () => {
    if (!challenge?.challengeId) return;
    setSubmitting(true);
    setNotice(EMPTY_NOTICE);
    try {
      const result = await checkPersonalIdentityVerification(challenge.challengeId, challenge.provider || signupProvider);
      setSignupResult(result);
      setNotice({ type: 'success', message: providerCopy(challenge.provider || signupProvider).verified });
    } catch (error) {
      setNotice({ type: 'error', message: getPersonalCodeAuthError(error) });
    } finally {
      setSubmitting(false);
    }
  }, [challenge, signupProvider]);

  const continueSignup = useCallback(async () => {
    if (!signupResult) return;
    setSubmitting(true);
    setNotice(EMPTY_NOTICE);
    try {
      await finishGallogSignup(signupResult);
    } catch (error) {
      setNotice({ type: 'error', message: getPersonalCodeAuthError(error) });
      setSubmitting(false);
    }
  }, [signupResult]);

  const copyText = useCallback(async (value) => {
    await navigator.clipboard.writeText(String(value || ''));
    setNotice({ type: 'success', message: '클립보드에 복사했습니다.' });
  }, []);

  return {
    mode,
    loginCode,
    signupProvider,
    identityValue,
    challenge,
    signupResult,
    submitting,
    notice,
    setLoginCode,
    setIdentityValue,
    changeMode,
    changeSignupProvider,
    submitLogin,
    startVerification,
    verifyIdentity,
    continueSignup,
    copyText,
  };
}
